/**
 * Game Actions Module (TypeScript)
 * Handles core Yatzy game logic (start, roll, hold, score)
 */

import { setText } from '../../lib/dom-utils';
import { UserAPI } from '../../utils/user-api';
import { logger } from '../../lib/logger';
import { handleCelebrations, type ApiResponseWithCelebrations } from '../../utils/celebration-handler';
import { handleFPRewards, processAchievementsOnly } from '../../utils/fp-rewards-handler';
import { balanceService } from '../../services/balance-service';
import type { AnimationTier } from '../../lib/animation-pref';

// Note: animateDiceRoll is imported from the JS version until dice-animator is migrated to TS
// @ts-ignore - dice-animator.js not yet migrated to TypeScript
import { animateDiceRoll } from '../../../js/lib/dice-animator.js';

/**
 * JSON response with error handling
 */
interface JsonResponse {
  data: any;
  raw: string;
}

/**
 * Game state object
 */
interface GameState {
  id: number;
  current_roll: number;
  total_score: number;
  game_state: string;
  dice_values?: number[];
  dice_held?: boolean[];
  [key: string]: any;
}

/**
 * Auth state
 */
interface AuthState {
  authenticated: boolean;
  [key: string]: any;
}

/**
 * User data
 */
interface UserData {
  preferences?: {
    yatzy_ruleset?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Parameters for updateMeta
 */
export interface UpdateMetaParams {
  game: GameState | null;
  rollCountEl: HTMLElement | null;
  totalScoreElements: NodeListOf<Element> | null;
  gameStateLabel: HTMLElement | null;
  startBtn: HTMLElement | null;
  rollBtn: HTMLButtonElement | null;
}

/**
 * Parameters for startGame
 */
export interface StartGameParams {
  rulesetId?: string | null;
  selectedRulesetId: string | null;
  authState: AuthState;
  userData: UserData | null;
  matchId: string | null;
  entryContext?: string;
  setStatus: (status: string) => void;
  logError: (msg: string, err: any) => void;
  startBtn: HTMLButtonElement | null;
}

/**
 * Parameters for roll
 */
export interface RollParams {
  game: GameState | null;
  holdMask: boolean[];
  animationLevel: AnimationTier;
  authState: AuthState;
  isRolling: boolean;
  shakeEnabled: boolean;
  shakeReadyTimeout: number | null;
  shakeDetector: any;
  rollBtn: HTMLButtonElement;
  setStatus: (status: string) => void;
  logError: (msg: string, err: any) => void;
  updateRollButtonText: () => void;
  startGameFn: () => Promise<GameState | null>;
}

/**
 * Roll result
 */
export interface RollResult {
  game: GameState | null;
  holdMask: boolean[];
  isRolling: boolean;
  shakeEnabled: boolean;
  shakeReadyTimeout: number | null;
}

/**
 * Parameters for toggleHold
 */
export interface ToggleHoldParams {
  game: GameState | null;
  holdMask: boolean[];
  idx: number;
  setStatus: (status: string) => void;
  logError: (msg: string, err: any) => void;
}

/**
 * Parameters for scoreCategory
 */
export interface ScoreCategoryParams {
  game: GameState | null;
  holdMask: boolean[];
  category: string;
  setStatus: (status: string) => void;
  logError: (msg: string, err: any) => void;
  showCenteredInfoBadge: (msg: string, duration: number) => void;
}

/**
 * Score category result
 */
export interface ScoreCategoryResult {
  game: GameState;
  holdMask: boolean[];
  fpEarned?: number;
  hypotheticalFP?: number;
  playEventId?: string;
  nextGameSuggestions?: Record<string, unknown>;
  apiResponse?: ApiResponseWithCelebrations;
}

/**
 * Helper to parse JSON response with error handling
 */
async function readJson(res: Response): Promise<JsonResponse> {
  const raw = await res.text();
  try {
    return { data: JSON.parse(raw), raw };
  } catch (err) {
    return { data: null, raw };
  }
}

/**
 * Update game metadata UI (roll count, total score, button states)
 */
export function updateMeta(params: UpdateMetaParams): void {
  const { 
    game, 
    rollCountEl, 
    totalScoreElements, 
    gameStateLabel, 
    startBtn, 
    rollBtn 
  } = params;
  
  const rollCountText = `${game?.current_roll || 0}/3`;
  if (rollCountEl) {
    setText(rollCountEl, rollCountText);
  }
  if (totalScoreElements) {
    totalScoreElements.forEach(el => setText(el as HTMLElement, String(game?.total_score ?? 0)));
  }
  if (gameStateLabel) setText(gameStateLabel, game?.game_state || 'Not started');

  // Show start button if no game started
  if (startBtn) {
    const hasStarted = !!game && game?.total_score !== undefined;
    startBtn.classList.toggle('d-none', hasStarted);
  }

  if (rollBtn) {
    const isDisabled = !game || (game?.current_roll ?? 0) >= 3 || game?.game_state === 'complete';
    rollBtn.disabled = isDisabled;
    // Visual feedback: add opacity when disabled
    if (isDisabled) {
      rollBtn.classList.add('opacity-50');
    } else {
      rollBtn.classList.remove('opacity-50');
    }
  }
}

/**
 * Start a new game
 */
export async function startGame(params: StartGameParams): Promise<GameState | null> {
  const {
    rulesetId = null, 
    selectedRulesetId,
    authState,
    userData,
    matchId,
    entryContext,
    setStatus,
    logError,
    startBtn
  } = params;
  
  if (startBtn) startBtn.disabled = true;
  
  // Use provided ruleset or fall back to selected/saved preference
  const ruleset = rulesetId || selectedRulesetId;
  
  try {
    const res = await fetch('/api/games/yatzy/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ruleset_id: ruleset,
        ...(matchId ? { match_id: matchId } : {}),
        ...(entryContext ? { entry_context: entryContext } : {}),
      })
    });
    const { data, raw } = await readJson(res);
    if (!res.ok || !data?.success) throw new Error(data?.error || raw || 'Failed to start game');
    const game = data.game;
    
    setStatus(game?.game_state || 'new');
    
    // Save preference to user account if authenticated
    if (authState.authenticated && userData && ruleset !== userData.preferences?.yatzy_ruleset) {
      try {
        await UserAPI.updatePreferences({ yatzy_ruleset: ruleset ?? undefined });
        if (userData.preferences) {
          userData.preferences.yatzy_ruleset = ruleset ?? undefined;
        }
      } catch (err) {
        // Failed to save preference
      }
    }
    
    return game;
  } catch (err) {
    logError('Start failed', err);
    return null;
  } finally {
    if (startBtn) startBtn.disabled = false;
  }
}

/**
 * Roll the dice
 */
export async function roll(
  params: RollParams, 
  eventOrDuration: number | Event | null = null
): Promise<RollResult> {
  let {
    game,
    holdMask,
    animationLevel,
    authState,
    isRolling,
    shakeEnabled,
    shakeReadyTimeout,
    shakeDetector,
    rollBtn,
    setStatus,
    logError,
    updateRollButtonText,
    startGameFn
  } = params;
  
  // Handle both event (from click handler) and duration (from shake)
  const customDuration = (eventOrDuration && typeof eventOrDuration === 'number') ? eventOrDuration : null;
  
  if (!game) {
    game = await startGameFn();
    if (!game) {
      return { game, holdMask, isRolling, shakeEnabled, shakeReadyTimeout };
    }
  }
  
  if (isRolling) {
    return { game, holdMask, isRolling, shakeEnabled, shakeReadyTimeout };
  }
  
  isRolling = true;
  rollBtn.disabled = true;
  
  // Only update the first span (the "Roll" text), not the entire button
  const rollBtnText = rollBtn.querySelector('span');
  if (rollBtnText) rollBtnText.textContent = 'Rolling...';
  
  try {
    const res = await fetch('/api/games/yatzy/roll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: game.id, hold_mask: holdMask })
    });
    
    const { data, raw } = await readJson(res);
    
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || raw || 'Roll failed');
    }
    
    game = data.game;
    holdMask = game?.dice_held || holdMask;
    
    // Update dice with animation
    const diceElements = Array.from(document.querySelectorAll('[data-index]')) as HTMLElement[];
    
    if (diceElements.length === 5 && game?.dice_values && game.dice_values.length === 5) {
      await animateDiceRoll(diceElements, game.dice_values, animationLevel, customDuration);
    }
    
    setStatus(game?.game_state || 'rolling');
  } catch (err) {
    logError('Roll failed', err);
  } finally {
    isRolling = false;
    rollBtn.disabled = false;
    updateRollButtonText();
    
    // Start 2-second cooldown to prevent accidental shake re-triggers
    if (shakeDetector) {
      shakeEnabled = false;
      clearTimeout(shakeReadyTimeout as number);
      shakeReadyTimeout = setTimeout(() => {
        shakeEnabled = true;
      }, 2000) as unknown as number;
    }
  }
  
  return { game, holdMask, isRolling, shakeEnabled, shakeReadyTimeout };
}

/**
 * Toggle hold state of a die
 */
export async function toggleHold(params: ToggleHoldParams): Promise<boolean[]> {
  const { game, holdMask, idx, setStatus, logError } = params;
  
  if (!game || game.game_state === 'complete') return holdMask;
  if ((game.current_roll ?? 0) === 0) {
    setStatus('Roll first to hold dice');
    return holdMask;
  }
  holdMask[idx] = !holdMask[idx];
  
  try {
    const res = await fetch('/api/games/yatzy/hold', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: game.id, held_mask: holdMask })
    });
    
    // Check response status
    if (!res.ok) {
      const { data, raw } = await readJson(res);
      throw new Error(data?.error || raw || 'Hold update failed');
    }
  } catch (err) {
    logError('Hold update failed', err);
  }
  
  return holdMask;
}

/**
 * Score a category
 */
export async function scoreCategory(params: ScoreCategoryParams): Promise<ScoreCategoryResult> {
  let { game, holdMask, category, setStatus, logError, showCenteredInfoBadge } = params;
  let lastFPEarned: number | undefined;
  let lastHypotheticalFP: number | undefined;
  let lastPlayEventId: string | undefined;
  let lastNextGameSuggestions: Record<string, unknown> | undefined;
  let lastApiResponse: ApiResponseWithCelebrations | undefined;
  
  if (!game) {
    throw new Error('Cannot score category: game state is null');
  }
  
  try {
    const res = await fetch('/api/games/yatzy/score', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: game.id, category })
    });
    const { data, raw } = await readJson(res);
    if (!res.ok || !data?.success) throw new Error(data?.error || raw || 'Score failed');
    game = data.game;
    holdMask = game?.dice_held || holdMask;
    setStatus(game?.game_state || 'scored');
    
    // Capture FP data and play_event_id for end-of-game overlay
    lastFPEarned = data.fp_earned ?? undefined;
    lastPlayEventId = data.play_event_id ?? undefined;
    lastHypotheticalFP = data.hypothetical_fp ?? undefined;
    lastNextGameSuggestions = data.next_game_suggestions ?? undefined;
    
    // Capture API response for celebration data (used by orchestrator on game completion)
    lastApiResponse = data as ApiResponseWithCelebrations;

    // Trigger celebrations if any (yatzy, bonus, high_score, match_win)
    handleCelebrations(data);
    
    // FIX-09: Skip FP toast when game is complete — end-of-game overlay shows FP.
    // Still refresh balance so navbar is up to date.
    if (game?.game_state === 'complete') {
      // Just refresh the balance, no FP toast
      balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
      // Still process achievement unlocks — end-of-game overlay doesn't show these
      processAchievementsOnly(data);
    } else {
      // Show FP earned toast or guest "could have earned" message (mid-game scoring)
      handleFPRewards(data);
    }
  } catch (err: any) {
    // Show a friendly info badge if user tries to score before rolling
    const msg = (err?.message || '').toLowerCase();
    if (msg.includes('roll at least once')) {
      showCenteredInfoBadge('Roll before scoring', 2000);
      // Don't log error to console - user is already notified with badge
    } else {
      logError('Score failed', err);
    }
  }
  
  if (!game) {
    throw new Error('Game state is null after scoring attempt');
  }
  
  return { game, holdMask, fpEarned: lastFPEarned, hypotheticalFP: lastHypotheticalFP, playEventId: lastPlayEventId, nextGameSuggestions: lastNextGameSuggestions, apiResponse: lastApiResponse };
}
